import React, { useState, useEffect, useCallback } from 'react';
import { Popup } from 'devextreme-react/popup';
import { ProgressBar } from 'devextreme-react/progress-bar';
import { Button } from 'devextreme-react/button';
import { ScrollView } from 'devextreme-react/scroll-view';
import { fetcher } from '../../actions';
import './NetsuiteProgressModal.css';
import PropTypes from 'prop-types';

export const NetsuiteProgressModal = ({ visible, onClose }) => {
  const [progressData, setProgressData] = useState(null);
  const [error, setError] = useState(null);
  const [isPolling, setIsPolling] = useState(false);

  // Poll for progress updates
  const fetchProgress = useCallback(async () => {
    const netsuiteApiUrl = process.env.REACT_APP_NETSUITE_API;
    try {
      const response = await fetcher(`${netsuiteApiUrl}/api/Netsuite/progress`);
      setProgressData(response);
      setError(null);
      
      console.log('Progress update received:', response); // Debug log
      
      // Continue polling unless there's an error or no active integration
      return response;
    } catch (err) {
      console.error('Error fetching progress:', err);
      setError('Failed to fetch integration progress');
      throw err;
    }
  }, []);

  // Manage polling lifecycle
  useEffect(() => {
    let intervalId;
    
    if (visible) {
      console.log('Starting progress polling...'); // Debug log
      setIsPolling(true);
      setError(null);
      
      // Fetch immediately when modal opens
      fetchProgress().catch(() => {
        // Error already handled in fetchProgress
      });
      
      // Set up continuous polling every 2 seconds
      intervalId = setInterval(async () => {
        try {
          const response = await fetchProgress();
          
          // Only stop polling if integration is truly complete or failed
          if (response && (response.status === 'COMPLETED' || response.status === 'FAILED')) {
            console.log('Integration finished, stopping polling:', response.status);
            setIsPolling(false);
            clearInterval(intervalId);
          }
        } catch (err) {
          // Error handled in fetchProgress, but stop polling on persistent errors
          setIsPolling(false);
          clearInterval(intervalId);
        }
      }, 2000);
    } else {
      // Reset state when modal is closed
      setIsPolling(false);
      setProgressData(null);
      setError(null);
    }
    
    return () => {
      if (intervalId) {
        console.log('Cleaning up polling interval');
        clearInterval(intervalId);
      }
      setIsPolling(false);
    };
  }, [visible, fetchProgress]);

  const formatDuration = (duration) => {
    if (!duration) return 'N/A';
    
    // Parse ISO 8601 duration format (e.g., "00:00:21.0668704")
    const parts = duration.split(':');
    if (parts.length >= 3) {
      const hours = parseInt(parts[0]);
      const minutes = parseInt(parts[1]);
      const seconds = Math.floor(parseFloat(parts[2]));
      
      if (hours > 0) {
        return `${hours}h ${minutes}m ${seconds}s`;
      } else if (minutes > 0) {
        return `${minutes}m ${seconds}s`;
      } else {
        return `${seconds}s`;
      }
    }
    return duration;
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return 'N/A';
    return new Date(timestamp).toLocaleString();
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'IN_PROGRESS':
        return '#0078d4';
      case 'COMPLETED':
        return '#107c10';
      case 'FAILED':
        return '#d13438';
      default:
        return '#605e5c';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'IN_PROGRESS':
        return 'Integration in Progress';
      case 'COMPLETED':
        return 'Integration Completed';
      case 'FAILED':
        return 'Integration Failed';
      default:
        return 'Unknown Status';
    }
  };

  const handleClose = () => {
    setIsPolling(false);
    onClose();
  };

  return (
    <Popup
      visible={visible}
      onHiding={handleClose}
      dragEnabled={false}
      closeOnOutsideClick={false}
      showCloseButton={true}
      showTitle={true}
      title="NetSuite Integration Progress"
      width={600}
      height={500}
      className="netsuite-progress-modal"
    >
      <div className="progress-container">
        {error ? (
          <div className="error-container">
            <div className="error-message">{error}</div>
            <Button
              text="Retry"
              type="default"
              onClick={() => {
                setError(null);
                setProgressData(null);
                fetchProgress().catch(() => {
                  // Error will be set in fetchProgress
                });
              }}
            />
          </div>
        ) : progressData ? (
          <>
            {/* Status Header */}
            <div className="status-header">
              <div className="status-badge" style={{ backgroundColor: getStatusColor(progressData.status) }}>
                {getStatusText(progressData.status)}
              </div>
              {progressData.status === 'IN_PROGRESS' && (
                <div className="status-indicator">
                  <div className="spinner"></div>
                </div>
              )}
            </div>

            {/* Progress Bar */}
            <div className="progress-section">
              <ProgressBar
                value={progressData.percentComplete || 0}
                showStatus={true}
                statusFormat={(ratio) => `${Math.round(ratio * 100)}% Complete`}
                className="main-progress-bar"
              />
            </div>

            {/* Current Task */}
            <div className="current-task-section">
              <h4>Current Task</h4>
              <div className="current-task">{progressData.currentTask || 'N/A'}</div>
              {progressData.details && (
                <div className="task-details">{progressData.details}</div>
              )}
            </div>

            {/* Time Information */}
            <div className="time-info-section">
              <div className="time-info-grid">
                <div className="time-info-item">
                  <span className="time-label">Started:</span>
                  <span className="time-value">{formatTimestamp(progressData.startTime)}</span>
                </div>
                {progressData.estimatedTimeRemaining && (
                  <div className="time-info-item">
                    <span className="time-label">Time Remaining:</span>
                    <span className="time-value">{formatDuration(progressData.estimatedTimeRemaining)}</span>
                  </div>
                )}
                <div className="time-info-item">
                  <span className="time-label">Last Updated:</span>
                  <span className="time-value">{formatTimestamp(progressData.lastUpdated)}</span>
                </div>
                {progressData.endTime && (
                  <div className="time-info-item">
                    <span className="time-label">Completed:</span>
                    <span className="time-value">{formatTimestamp(progressData.endTime)}</span>
                  </div>
                )}
              </div>
            </div>

            {/* Completed Tasks */}
            {progressData.completedTasks && progressData.completedTasks.length > 0 && (
              <div className="completed-tasks-section">
                <h4>Completed Tasks ({progressData.completedTasks.length})</h4>
                <ScrollView height={150} className="completed-tasks-scroll">
                  <div className="completed-tasks-list">
                    {progressData.completedTasks.map((task, index) => (
                      <div key={index} className="completed-task-item">
                        <div className="task-checkmark">✓</div>
                        <div className="task-name">{task}</div>
                      </div>
                    ))}
                  </div>
                </ScrollView>
              </div>
            )}

            {/* Action Buttons */}
            <div className="modal-actions">
              <Button
                text="Refresh"
                type="normal"
                onClick={() => fetchProgress().catch(() => {})}
                disabled={isPolling}
                style={{ marginRight: '10px' }}
              />
              <Button
                text={progressData.status === 'IN_PROGRESS' ? 'Hide Progress' : 'Close'}
                type="default"
                onClick={handleClose}
              />
            </div>
          </>
        ) : (
          <div className="loading-container">
            <div className="spinner"></div>
            <div>Loading integration progress...</div>
          </div>
        )}
      </div>
    </Popup>
  );
};

NetsuiteProgressModal.propTypes = {
  visible: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired
};

export default NetsuiteProgressModal;